<template>
  <div class="developer-view">
    <h1 class="text-h4 mb-4">{{ t('dev.title') }}</h1>
    
    <!-- Aviso: carteira necessária -->
    <v-alert
      v-if="showAuthWarning"
      type="warning"
      variant="tonal"
      closable
      class="mb-4"
      @click:close="showAuthWarning = false"
    >
      <div class="d-flex align-center">
        <v-icon class="mr-2">mdi-wallet</v-icon>
        <div>
          <strong>{{ t('wallet.connect') }}</strong> — 
          {{ $locale === 'en' ? 'You need to connect a wallet to access the IDE and manage tokens.' : 
             $locale === 'es' ? 'Necesita conectar una billetera para acceder al IDE y gestionar tokens.' :
             'Você precisa conectar uma carteira para acessar o IDE e gerenciar tokens.' }}
        </div>
      </div>
    </v-alert>
    
    <v-row>
      <v-col cols="12" sm="6" md="4">
        <v-card
          :to="walletConnected ? { name: 'developer-tokens' } : undefined"
          :disabled="!walletConnected"
          height="200"
          class="card-hover"
          @click="!walletConnected && (showAuthWarning = true)"
        >
          <v-card-text class="d-flex flex-column align-center justify-center h-100 text-center">
            <v-icon size="60" :color="walletConnected ? 'primary' : 'grey'" class="mb-4">mdi-currency-token</v-icon>
            <div class="text-h6">{{ t('dev.myTokens') }}</div>
            <div class="text-body-2 mt-2">{{ t('dev.manageTokens') }}</div>
            <v-chip v-if="!walletConnected" size="x-small" color="warning" class="mt-2">
              <v-icon size="12" start>mdi-lock</v-icon>
              {{ t('wallet.connect') }}
            </v-chip>
          </v-card-text>
        </v-card>
      </v-col>
      
      <v-col cols="12" sm="6" md="4">
        <v-card
          :to="walletConnected ? { name: 'developer-ide' } : undefined"
          :disabled="!walletConnected"
          height="200"
          class="card-hover"
          @click="!walletConnected && (showAuthWarning = true)"
        >
          <v-card-text class="d-flex flex-column align-center justify-center h-100 text-center">
            <v-icon size="60" color="primary" class="mb-4">mdi-code-braces</v-icon>
            <div class="text-h6">{{ t('dev.queenIde') }}</div>
            <div class="text-body-2 mt-2">{{ t('dev.ideDesc') }}</div>
          </v-card-text>
        </v-card>
      </v-col>
      
      <v-col cols="12" sm="6" md="4">
        <v-card
          href="https://docs.quarnx.com"
          target="_blank"
          height="200"
          class="card-hover"
        >
          <v-card-text class="d-flex flex-column align-center justify-center h-100 text-center">
            <v-icon size="60" color="primary" class="mb-4">mdi-book-open-variant</v-icon>
            <div class="text-h6">{{ t('nav.docs') }}</div>
            <div class="text-body-2 mt-2">{{ t('dev.docsDesc') }}</div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    
    <!-- Estatísticas do desenvolvedor -->
    <h2 class="text-h5 mt-8 mb-4">{{ t('dev.myStats') }}</h2>
    
    <v-row>
      <v-col cols="12" md="3">
        <v-card class="text-center py-4" height="100%">
          <div class="text-overline">{{ t('dev.tokensCreated') }}</div>
          <div class="text-h3 my-2 font-weight-bold">{{ tokensCount }}</div>
          <v-divider class="mx-4 my-1"></v-divider>
          <div class="text-caption mt-2">
            Total de tokens QRC20 que você criou
          </div>
        </v-card>
      </v-col>
      
      <v-col cols="12" md="3">
        <v-card class="text-center py-4" height="100%">
          <div class="text-overline">{{ t('dev.activeContracts') }}</div>
          <div class="text-h3 my-2 font-weight-bold">{{ contractsCount }}</div>
          <v-divider class="mx-4 my-1"></v-divider>
          <div class="text-caption mt-2">
            Contratos implantados em produção
          </div>
        </v-card>
      </v-col>
      
      <v-col cols="12" md="3">
        <v-card class="text-center py-4" height="100%">
          <div class="text-overline">{{ t('tx.title') }}</div>
          <div class="text-h3 my-2 font-weight-bold">{{ txCount }}</div>
          <v-divider class="mx-4 my-1"></v-divider>
          <div class="text-caption mt-2">
            Transações nas últimas 24 horas
          </div>
        </v-card>
      </v-col>
      
      <v-col cols="12" md="3">
        <v-card class="text-center py-4" height="100%">
          <div class="text-overline">{{ t('dev.valueTransferred') }}</div>
          <div class="text-h3 my-2 font-weight-bold">{{ formatValue(totalValue) }}</div>
          <v-divider class="mx-4 my-1"></v-divider>
          <div class="text-caption mt-2">
            Total em QRX movimentado hoje
          </div>
        </v-card>
      </v-col>
    </v-row>
    
    <!-- Recursos para desenvolvedores -->
    <h2 class="text-h5 mt-8 mb-4">{{ t('dev.resources') }}</h2>
    
    <v-row>
      <v-col cols="12" md="6">
        <v-card>
          <v-card-title class="d-flex align-center">
            <v-icon color="primary" class="mr-2">mdi-api</v-icon>
            APIs e SDKs
          </v-card-title>
          <v-divider></v-divider>
          <v-list lines="two">
            <v-list-item
              v-for="(item, index) in apiResources"
              :key="index"
              :href="item.url"
              target="_blank"
            >
              <template v-slot:prepend>
                <v-avatar color="grey-lighten-1" class="me-3">
                  <v-icon color="white">{{ item.icon }}</v-icon>
                </v-avatar>
              </template>
              <v-list-item-title>{{ item.title }}</v-list-item-title>
              <v-list-item-subtitle>{{ item.subtitle }}</v-list-item-subtitle>
            </v-list-item>
          </v-list>
        </v-card>
      </v-col>
      
      <v-col cols="12" md="6">
        <v-card>
          <v-card-title class="d-flex align-center">
            <v-icon color="primary" class="mr-2">mdi-school</v-icon>
            Tutoriais e Exemplos
          </v-card-title>
          <v-divider></v-divider>
          <v-list lines="two">
            <v-list-item
              v-for="(item, index) in tutorialResources"
              :key="index"
              :href="item.url"
              target="_blank"
            >
              <template v-slot:prepend>
                <v-avatar
                  :color="item.level === 'beginner' ? 'success' : item.level === 'intermediate' ? 'warning' : 'error'"
                  class="me-3"
                >
                  <span class="text-uppercase text-body-2 text-white">{{ item.level.charAt(0) }}</span>
                </v-avatar>
              </template>
              <v-list-item-title>{{ item.title }}</v-list-item-title>
              <v-list-item-subtitle>{{ item.subtitle }}</v-list-item-subtitle>
            </v-list-item>
          </v-list>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { useDeveloperStore } from '@/stores/developer'
import { useWalletStore } from '@/stores/wallet'

export default {
  name: 'DeveloperView',
  data() {
    return {
      showAuthWarning: this.$route?.query?.auth === 'required',
      apiResources: [
        {
          title: 'API REST QuarnX',
          subtitle: 'Documentação completa da API REST para interação com a blockchain',
          icon: 'mdi-api',
          url: 'https://docs.quarnx.com/api-reference'
        },
        {
          title: 'SDK JavaScript',
          subtitle: 'Pacote NPM para integração com aplicativos JavaScript e Node.js',
          icon: 'mdi-nodejs',
          url: 'https://www.npmjs.com/package/quarnx-sdk'
        },
        {
          title: 'SDK Python',
          subtitle: 'Biblioteca Python para interagir com a blockchain QuarnX',
          icon: 'mdi-language-python',
          url: 'https://pypi.org/project/quarnx-python/'
        },
        {
          title: 'Web3 Provider',
          subtitle: 'Adaptador Web3 para integração com aplicações Ethereum existentes',
          icon: 'mdi-ethereum',
          url: 'https://docs.quarnx.com/web3-provider'
        }
      ],
      tutorialResources: [
        {
          title: 'Começando com Queen',
          subtitle: 'Introdução à linguagem de contratos inteligentes da QuarnX',
          level: 'beginner',
          url: 'https://docs.quarnx.com/tutorials/queen-basics'
        },
        {
          title: 'Criando um Token QRC20',
          subtitle: 'Passo a passo para criar e implantar seu próprio token',
          level: 'beginner',
          url: 'https://docs.quarnx.com/tutorials/create-qrc20'
        },
        {
          title: 'DApps com QuarnX',
          subtitle: 'Desenvolvimento de aplicações descentralizadas completas',
          level: 'intermediate',
          url: 'https://docs.quarnx.com/tutorials/dapps'
        },
        {
          title: 'Avançado: Protocolos DeFi',
          subtitle: 'Implementando protocolos financeiros descentralizados',
          level: 'advanced',
          url: 'https://docs.quarnx.com/tutorials/defi-protocols'
        }
      ]
    }
  },
  computed: {
    developerStore() {
      return useDeveloperStore()
    },
    walletConnected() {
      return useWalletStore().hasWallet
    },
    tokensCount() {
      return this.developerStore.tokens?.length || 0
    },
    contractsCount() {
      return this.developerStore.contracts?.length || 0
    },
    txCount() {
      // Dados reais da blockchain store
      const store = this.blockchainStore
      return store?.stats?.totalTransactions || 0
    },
    totalValue() {
      // Dados reais: total supply em KinX
      const store = this.blockchainStore
      const nanoValue = store?.stats?.totalSupply || 0
      return nanoValue / 1_000_000_000
    },
    blockchainStore() {
      const { useBlockchainStore } = require('@/stores/blockchain')
      return useBlockchainStore()
    }
  },
  methods: {
    formatValue(value) {
      return new Intl.NumberFormat('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(value)
    }
  },
  async mounted() {
    await this.developerStore.fetchTokens()
    await this.developerStore.fetchContracts()
  }
}
</script>

<style scoped>
.developer-view {
  padding-bottom: 32px;
}

.card-hover {
  transition: transform 0.3s, box-shadow 0.3s;
}

.card-hover:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2) !important;
}
</style>
